/**
 * Fix FJ (2026-10-08): "En la parte de Adelanto, que pase a ser «ADELANTO / DESCUENTO». Que
 * existan dos Botones: +Nuevo Adelanto y -Nuevo Descuento" (CEN).
 * - Adelanto: lo carga RRHH, lo aprueba quien se designa en Config HR (si no hay nadie, Gerencia
 *   Técnica y Director) y RRHH pide la OP a Tesorería con los aprobados. Pagada la OP, el
 *   adelanto queda pagado y la liquidación del período lo descuenta.
 * - Descuento: a quién, monto total, en cuántas cuotas y la descripción. Misma aprobación. Cada
 *   liquidación descuenta una cuota hasta saldarlo.
 * - Filtros por turno, obra y empleado (como Personal y Parte Diario) y por fecha inicio / fin.
 */
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Minus, Loader2, CheckCircle2, Trash2, DollarSign, Send, X } from "lucide-react";
import { API, useAuth } from "../../contexts/AuthContext";
import { formatPYG, formatPYGCompact } from "../../utils/format";
import { Skeleton } from "../../components/ui/skeleton";
import SelectBuscable from "../../components/SelectBuscable";
import SelectorEmpleados from "./SelectorEmpleados";
import { useTurnos, filtrarEmpleados, idsACargar, SelectTurno } from "./FiltroTurnoObra";

const inputCls = "w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#2E6DA4]/20 focus:border-[#2E6DA4] bg-white transition-all";
const labelCls = "block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5";

const ESTADOS = {
  pendiente: { label: "Pendiente", cls: "bg-amber-100 text-amber-700 border-amber-200" },
  aprobado: { label: "Aprobado", cls: "bg-blue-100 text-blue-700 border-blue-200" },
  op_solicitada: { label: "OP solicitada", cls: "bg-indigo-100 text-indigo-700 border-indigo-200" },
  pagado: { label: "Pagado", cls: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  descontado: { label: "Descontado", cls: "bg-gray-100 text-gray-600 border-gray-200" },
};
const estadoDe = (a) => (a.status === "aprobado" && a.op_id ? "op_solicitada" : a.status);
const hoy = () => new Date().toISOString().split("T")[0];
const inicioMes = () => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().split("T")[0]; };
const formVacio = (tipo) => ({ tipo, worker_id: "", monto: "", quincena: "primera", fecha: hoy(), motivo: "", project_id: "", cuotas: 1 });

export default function AdelantosDescuentosTab({ workers, projects }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null);   // null | formulario de adelanto o descuento
  const [saving, setSaving] = useState(false);
  const [pidiendo, setPidiendo] = useState(false);
  const [puedeAprobar, setPuedeAprobar] = useState(false);
  // Filtros
  const turnos = useTurnos();
  const [turnoId, setTurnoId] = useState("");
  const [projectId, setProjectId] = useState("");
  const [workerIds, setWorkerIds] = useState([]);
  const [desde, setDesde] = useState(inicioMes());
  const [hasta, setHasta] = useState(hoy());
  const [tipo, setTipo] = useState("");
  const [estado, setEstado] = useState("todos");

  const activeWorkers = (workers || []).filter(w => w.status === "activo");
  const activeProjects = (projects || []).filter(p => p.status === "activo");

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (desde) params.desde = desde;
      if (hasta) params.hasta = hasta;
      if (tipo) params.tipo = tipo;
      const res = await axios.get(`${API}/hr/adelantos`, { params, withCredentials: true });
      setItems(res.data || []);
    } catch { toast.error("Error al cargar adelantos y descuentos"); }
    finally { setLoading(false); }
  }, [desde, hasta, tipo]);
  useEffect(() => { cargar(); }, [cargar]);
  useEffect(() => {
    axios.get(`${API}/hr/adelantos/aprobadores`, { withCredentials: true })
      .then(({ data }) => setPuedeAprobar(!!data.puede_aprobar)).catch(() => setPuedeAprobar(false));
  }, []);

  // Turno, obra y empleado se filtran sobre el legajo del trabajador.
  const filtroActivo = turnoId || projectId || workerIds.length;
  const idsFiltro = filtroActivo ? new Set(idsACargar(workers, workerIds, turnoId, projectId)) : null;
  const visibles = items.filter(a => (!idsFiltro || idsFiltro.has(a.worker_id))
    && (estado === "todos" || estadoDe(a) === estado || (estado === "aprobado" && estadoDe(a) === "op_solicitada")));

  const guardar = async () => {
    const worker = activeWorkers.find(w => (w.id || w._id) === form.worker_id);
    if (!form.worker_id || !form.monto || !form.fecha) { toast.error("Completá trabajador, monto y fecha"); return; }
    if (form.tipo === "descuento" && !form.motivo.trim()) { toast.error("Cargá la descripción del descuento"); return; }
    setSaving(true);
    try {
      const proj = (projects || []).find(p => (p._id || p.id) === form.project_id);
      await axios.post(`${API}/hr/adelantos`, {
        ...form, worker_name: worker?.name || "", monto: parseFloat(form.monto),
        cuotas: form.tipo === "descuento" ? Number(form.cuotas) || 1 : 1,
        quincena: form.tipo === "descuento" ? "" : form.quincena, project_name: proj?.name || "",
      }, { withCredentials: true });
      toast.success(form.tipo === "descuento" ? "Descuento registrado: queda pendiente de aprobación" : "Adelanto registrado: queda pendiente de aprobación");
      setForm(null);
      cargar();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al registrar"); }
    finally { setSaving(false); }
  };

  const aprobar = async (a) => {
    try {
      await axios.put(`${API}/hr/adelantos/${a.id}/approve`, {}, { withCredentials: true });
      toast.success(a.tipo === "descuento" ? "Descuento aprobado" : "Adelanto aprobado");
      cargar();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };
  const eliminar = async (a) => {
    if (!window.confirm(`¿Eliminar el ${a.tipo === "descuento" ? "descuento" : "adelanto"} de ${a.worker_name}?`)) return;
    try {
      await axios.delete(`${API}/hr/adelantos/${a.id}`, { withCredentials: true });
      toast.success("Eliminado");
      cargar();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  // Adelantos aprobados que todavía no tienen su pedido de OP (de los que se ven).
  const paraPedido = visibles.filter(a => a.tipo !== "descuento" && a.status === "aprobado" && !a.op_id);
  const totalPedido = paraPedido.reduce((s, a) => s + Number(a.monto || 0), 0);
  const pedirOP = async () => {
    if (!paraPedido.length) return;
    if (!window.confirm(`¿Generar el Pedido de OP para ${paraPedido.length} adelanto(s) aprobado(s)? Total ${formatPYG(totalPedido)}. Llega a Tesorería → Solicitudes.`)) return;
    setPidiendo(true);
    try {
      const { data } = await axios.post(`${API}/hr/adelantos/pedido-op`, { ids: paraPedido.map(a => a.id) }, { withCredentials: true });
      toast.success(`Pedido de OP ${data.number} enviado a Tesorería · ${formatPYG(data.total)}`);
      cargar();
    } catch (err) { toast.error(err.response?.data?.detail || "No se pudo generar el pedido de OP"); }
    finally { setPidiendo(false); }
  };

  const sumar = (f) => visibles.filter(f).reduce((s, a) => s + Number(a.monto || 0), 0);
  const kpis = [
    { label: "Pendientes", value: sumar(a => a.status === "pendiente"), count: visibles.filter(a => a.status === "pendiente").length, cls: "text-amber-600" },
    { label: "Aprobados", value: sumar(a => a.status === "aprobado" && a.tipo !== "descuento"), count: visibles.filter(a => a.status === "aprobado" && a.tipo !== "descuento").length, cls: "text-blue-600" },
    { label: "Pagados", value: sumar(a => a.status === "pagado"), count: visibles.filter(a => a.status === "pagado").length, cls: "text-emerald-600" },
    { label: "Descuentos por cobrar", value: visibles.filter(a => a.tipo === "descuento" && a.status === "aprobado").reduce((s, a) => s + Number(a.saldo || 0), 0),
      count: visibles.filter(a => a.tipo === "descuento" && a.status === "aprobado").length, cls: "text-red-600" },
  ];

  return (
    <div className="space-y-5" data-testid="adelantos-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-bold text-gray-900">Adelantos / Descuentos</h2>
          <p className="text-xs text-gray-500 mt-0.5">Se aprueban según Config HR · los adelantos aprobados van a Tesorería con el Pedido de OP · la liquidación descuenta los adelantos pagados del período y una cuota de cada descuento</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setForm(formVacio("adelanto"))} className="flex items-center gap-2 bg-[#2E6DA4] hover:bg-[#245A8A] text-white px-4 py-2.5 rounded-xl text-sm font-semibold shadow-sm" data-testid="new-adelanto-btn">
            <Plus size={15} /> Nuevo Adelanto
          </button>
          <button onClick={() => setForm(formVacio("descuento"))} className="flex items-center gap-2 bg-white border border-red-300 text-red-700 hover:bg-red-50 px-4 py-2.5 rounded-xl text-sm font-semibold shadow-sm" data-testid="new-descuento-btn">
            <Minus size={15} /> Nuevo Descuento
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map(c => (
          <div key={c.label} className="bg-white rounded-xl border border-gray-200/60 p-4 shadow-sm" data-testid={`adelanto-kpi-${c.label.split(" ")[0].toLowerCase()}`}>
            <p className={`text-xs font-bold uppercase tracking-wide mb-1 ${c.cls}`}>{c.label}</p>
            <p className="text-lg font-bold text-gray-900">{formatPYGCompact(c.value)}</p>
            <p className="text-xs text-gray-400">{c.count} registro(s)</p>
          </div>
        ))}
      </div>

      {form && (
        <div className={`bg-white rounded-2xl border shadow-md p-5 ${form.tipo === "descuento" ? "border-red-200" : "border-[#2E6DA4]/20"}`} data-testid="adelanto-form">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2">
              {form.tipo === "descuento" ? <><Minus size={16} className="text-red-600" /> Nuevo Descuento</> : <><DollarSign size={16} className="text-[#2E6DA4]" /> Nuevo Adelanto</>}
            </h3>
            <button onClick={() => setForm(null)} className="p-1.5 rounded-lg hover:bg-gray-100"><X size={15} /></button>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2 sm:col-span-1">
              <label className={labelCls}>{form.tipo === "descuento" ? "Se le descuenta a *" : "Trabajador *"}</label>
              <select value={form.worker_id} onChange={e => setForm(f => ({ ...f, worker_id: e.target.value }))} className={inputCls} data-testid="adelanto-worker-select">
                <option value="">Seleccionar trabajador...</option>
                {activeWorkers.map(w => <option key={w.id || w._id} value={w.id || w._id}>{`${w.name} — ${w.role_title || ""}`}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>{form.tipo === "descuento" ? "Monto total (PYG) *" : "Monto (PYG) *"}</label>
              <input type="number" value={form.monto} onChange={e => setForm(f => ({ ...f, monto: e.target.value }))} placeholder="0" className={inputCls} data-testid="adelanto-monto-input" />
            </div>
            {form.tipo === "descuento" ? (
              <div>
                <label className={labelCls}>En cuántos pagos (cuotas) *</label>
                <input type="number" min="1" max="60" value={form.cuotas} onChange={e => setForm(f => ({ ...f, cuotas: e.target.value }))} className={inputCls} data-testid="descuento-cuotas-input" />
                {Number(form.monto) > 0 && Number(form.cuotas) > 0 && (
                  <p className="text-[11px] text-gray-500 mt-1" data-testid="descuento-cuota-texto">Cuota de {formatPYG(Math.round(Number(form.monto) / Number(form.cuotas)))} por liquidación</p>
                )}
              </div>
            ) : (
              <div>
                <label className={labelCls}>Quincena *</label>
                <select value={form.quincena} onChange={e => setForm(f => ({ ...f, quincena: e.target.value }))} className={inputCls} data-testid="adelanto-quincena-select">
                  <option value="primera">1ª Quincena (1–15)</option>
                  <option value="segunda">2ª Quincena (16–31)</option>
                </select>
              </div>
            )}
            <div>
              <label className={labelCls}>{form.tipo === "descuento" ? "Se descuenta desde *" : "Fecha *"}</label>
              <input type="date" value={form.fecha} onChange={e => setForm(f => ({ ...f, fecha: e.target.value }))} className={inputCls} data-testid="adelanto-fecha-input" />
            </div>
            {form.tipo !== "descuento" && (
              <div className="col-span-2 sm:col-span-1">
                <label className={labelCls}>Proyecto</label>
                <select value={form.project_id} onChange={e => setForm(f => ({ ...f, project_id: e.target.value }))} className={inputCls}>
                  <option value="">Sin proyecto...</option>
                  {(projects || []).map(p => <option key={p._id || p.id} value={p._id || p.id}>{p.name}</option>)}
                </select>
              </div>
            )}
            <div className="col-span-2">
              <label className={labelCls}>{form.tipo === "descuento" ? "Descripción *" : "Motivo / Observación"}</label>
              <input value={form.motivo} onChange={e => setForm(f => ({ ...f, motivo: e.target.value }))}
                placeholder={form.tipo === "descuento" ? "Ej.: herramienta extraviada" : "Motivo del adelanto..."} className={inputCls} data-testid="adelanto-motivo-input" />
            </div>
          </div>
          <div className="flex gap-3 mt-4">
            <button onClick={() => setForm(null)} className="flex-1 border border-gray-300 text-gray-600 hover:bg-gray-50 rounded-xl py-2.5 text-sm font-medium">Cancelar</button>
            <button onClick={guardar} disabled={saving} className={`flex-1 text-white rounded-xl py-2.5 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60 ${form.tipo === "descuento" ? "bg-red-600 hover:bg-red-700" : "bg-[#2E6DA4] hover:bg-[#245A8A]"}`} data-testid="save-adelanto-btn">
              {saving ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle2 size={15} />}
              {saving ? "Guardando..." : form.tipo === "descuento" ? "Registrar Descuento" : "Registrar Adelanto"}
            </button>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-200/60 p-4 shadow-sm space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-6 gap-3">
          <div><label className={labelCls}>Turno</label><SelectTurno turnos={turnos} value={turnoId} onChange={setTurnoId} className={inputCls} testid="adelanto-filter-turno" /></div>
          <div><label className={labelCls}>Obra</label>
            <select value={projectId} onChange={e => setProjectId(e.target.value)} className={inputCls} data-testid="adelanto-filter-obra">
              <option value="">Todas</option>
              {activeProjects.map(p => <option key={p._id || p.id} value={p._id || p.id}>{p.name}</option>)}
            </select>
          </div>
          <div className="sm:col-span-2"><label className={labelCls}>Empleado</label>
            <SelectorEmpleados workers={filtrarEmpleados(workers, turnoId, projectId)} value={workerIds} onChange={setWorkerIds} testid="adelanto-filter-empleado" placeholder="Todos" />
          </div>
          <div><label className={labelCls}>Fecha inicio</label><input type="date" value={desde} onChange={e => setDesde(e.target.value)} className={inputCls} data-testid="adelanto-filter-desde" /></div>
          <div><label className={labelCls}>Fecha fin</label><input type="date" value={hasta} onChange={e => setHasta(e.target.value)} className={inputCls} data-testid="adelanto-filter-hasta" /></div>
        </div>
        <div className="flex flex-wrap gap-1.5 items-center">
          {[["", "Adelantos y descuentos"], ["adelanto", "Adelantos"], ["descuento", "Descuentos"]].map(([v, l]) => (
            <button key={v || "todos"} onClick={() => setTipo(v)} className={`px-3 py-2 text-xs font-semibold rounded-xl border ${tipo === v ? "bg-gray-800 text-white border-gray-800" : "bg-white text-gray-500 border-gray-200"}`} data-testid={`adelanto-tipo-${v || "todos"}`}>{l}</button>
          ))}
          <span className="w-px h-6 bg-gray-200 mx-1" />
          {["todos", "pendiente", "aprobado", "pagado", "descontado"].map(s => (
            <button key={s} onClick={() => setEstado(s)} className={`px-3 py-2 text-xs font-semibold rounded-xl border ${estado === s ? "bg-[#2E6DA4] text-white border-[#2E6DA4]" : "bg-white text-gray-500 border-gray-200"}`} data-testid={`adelanto-filter-${s}`}>
              {s === "todos" ? "Todos" : ESTADOS[s].label}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-gray-200/60 shadow-sm overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-100 bg-gray-50/50 flex items-center justify-between gap-3 flex-wrap">
          <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">Registro de adelantos y descuentos <span className="text-gray-400 normal-case font-normal">· {visibles.length} registro(s)</span></p>
          <button onClick={pedirOP} disabled={!paraPedido.length || pidiendo}
            className="flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white px-3 py-2 rounded-xl text-xs font-bold" data-testid="adelanto-pedido-op-btn">
            {pidiendo ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
            Generar Pedido de OP ({paraPedido.length}{paraPedido.length ? ` · ${formatPYG(totalPedido)}` : ""})
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead><tr className="border-b border-gray-100">
              {["Fecha", "Tipo", "Trabajador", "Detalle", "Motivo", "Monto", "Estado", "Acciones"].map(h => (
                <th key={h} className="text-left px-4 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {loading ? Array.from({ length: 3 }).map((_, i) => (
                <tr key={i} className="border-b border-gray-50">{Array.from({ length: 8 }).map((__, j) => <td key={j} className="px-4 py-3"><Skeleton className="h-4 w-full" /></td>)}</tr>
              )) : visibles.length === 0 ? (
                <tr><td colSpan={8} className="text-center py-12 text-gray-400 text-sm"><DollarSign size={32} className="mx-auto mb-2 opacity-20" /><p>No hay adelantos ni descuentos con estos filtros</p></td></tr>
              ) : visibles.map(a => {
                const st = ESTADOS[estadoDe(a)] || { label: a.status, cls: "bg-gray-100 text-gray-600 border-gray-200" };
                const esDesc = a.tipo === "descuento";
                return (
                  <tr key={a.id} className="border-b border-gray-50 hover:bg-gray-50/40" data-testid={`adelanto-row-${a.id}`}>
                    <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{a.fecha}</td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold border ${esDesc ? "bg-red-50 text-red-700 border-red-200" : "bg-sky-50 text-sky-700 border-sky-200"}`}>{esDesc ? "Descuento" : "Adelanto"}</span>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm font-semibold text-gray-900">{a.worker_name}</p>
                      {a.project_name && <p className="text-xs text-gray-400">{a.project_name}</p>}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-600 whitespace-nowrap" data-testid={`adelanto-detalle-${a.id}`}>
                      {esDesc
                        ? <>{a.cuotas_pagadas || 0} de {a.cuotas || 1} cuota(s) · saldo {formatPYG(a.saldo ?? a.monto)}</>
                        : a.quincena ? (a.quincena === "primera" ? "1ª Q (1–15)" : "2ª Q (16–31)") : "—"}
                      {a.op_number && <p className="text-[10px] text-indigo-600 font-semibold">{a.op_number}</p>}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500 max-w-[180px] truncate" title={a.motivo || ""}>{a.motivo || "—"}</td>
                    <td className={`px-4 py-3 font-bold whitespace-nowrap ${esDesc ? "text-red-700" : "text-gray-900"}`}>{esDesc ? "− " : ""}{formatPYGCompact(a.monto)}</td>
                    <td className="px-4 py-3"><span className={`text-xs px-2.5 py-1 rounded-full font-semibold border whitespace-nowrap ${st.cls}`} data-testid={`adelanto-estado-${a.id}`}>{st.label}</span></td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        {a.status === "pendiente" && puedeAprobar && (
                          <button onClick={() => aprobar(a)} className="flex items-center gap-1 px-2 py-1.5 bg-blue-600 text-white text-xs font-semibold rounded-lg hover:bg-blue-700" data-testid={`approve-adelanto-${a.id}`} title="Aprobar">
                            <CheckCircle2 size={11} /> APROB
                          </button>
                        )}
                        {a.status === "pendiente" && (
                          <button onClick={() => eliminar(a)} className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg" data-testid={`delete-adelanto-${a.id}`} title="Eliminar">
                            <Trash2 size={13} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// Fix FJ (2026-10-08): "Agregar en Configuración HR un espacio donde designar que usuarios
// pueden aprobar para modificaciones en un futuro" (CEN).
export function AprobadoresAdelantosCard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [sel, setSel] = useState([]);
  const [guardando, setGuardando] = useState(false);
  const puedeEditar = ["Superadministrador", "Administrador"].includes(user?.role);
  useEffect(() => {
    axios.get(`${API}/hr/adelantos/aprobadores`, { withCredentials: true })
      .then(({ data }) => { setData(data); setSel(data.user_ids || []); })
      .catch(() => setData({ usuarios: [], user_ids: [], roles_por_defecto: [] }));
  }, []);
  if (!data) return null;
  const nombre = (id) => (data.usuarios.find(u => u.id === id) || {}).name || id;
  const guardar = async () => {
    setGuardando(true);
    try {
      const { data: d } = await axios.put(`${API}/hr/adelantos/aprobadores`, { user_ids: sel }, { withCredentials: true });
      setData(d); setSel(d.user_ids || []);
      toast.success("Aprobadores de adelantos y descuentos guardados");
    } catch (err) { toast.error(err.response?.data?.detail || "No se pudo guardar"); }
    finally { setGuardando(false); }
  };
  return (
    <div className="bg-white rounded-xl border border-gray-200/60 shadow-sm p-5 space-y-3" data-testid="aprobadores-adelantos-card">
      <div>
        <p className="text-sm font-bold text-gray-800">Aprobación de adelantos y descuentos</p>
        <p className="text-xs text-gray-500 mt-0.5">
          {sel.length ? "Solo estos usuarios (y el Superadministrador) aprueban." : `Sin usuarios elegidos, aprueban ${data.roles_por_defecto.includes("Director") ? "Gerencia Técnica y Director" : data.roles_por_defecto.join(", ")}.`}
        </p>
      </div>
      <div className="flex flex-wrap gap-1.5" data-testid="aprobadores-adelantos-lista">
        {sel.map(id => (
          <span key={id} className="inline-flex items-center gap-1 bg-[#EBF2F9] text-[#1A4B7A] text-xs font-semibold px-2 py-1 rounded-full">
            {nombre(id)}
            {puedeEditar && <button onClick={() => setSel(s => s.filter(x => x !== id))} className="hover:text-red-600"><X size={11} /></button>}
          </span>
        ))}
        {!sel.length && <span className="text-xs text-gray-400">Nadie elegido</span>}
      </div>
      {puedeEditar && (
        <div className="flex gap-2 items-center">
          <div className="flex-1">
            <SelectBuscable value="" onChange={v => v && setSel(s => (s.includes(v) ? s : [...s, v]))} vacio="+ Agregar usuario…"
              options={data.usuarios.filter(u => !sel.includes(u.id)).map(u => ({ value: u.id, label: `${u.name} · ${u.role}` }))}
              className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm bg-white" testId="aprobadores-adelantos-agregar" />
          </div>
          <button onClick={guardar} disabled={guardando} className="bg-[#2E6DA4] hover:bg-[#245A8A] text-white px-4 py-2 rounded-xl text-sm font-semibold disabled:opacity-60" data-testid="aprobadores-adelantos-guardar">
            {guardando ? "Guardando..." : "Guardar"}
          </button>
        </div>
      )}
    </div>
  );
}
